import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { apiCall } from '../utils/api';

const CustomerAuthContext = createContext();

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const authInitializedRef = useRef(false);

  const normalizeCustomer = (raw) => {
    if (!raw) return null;
    const normalizedId = raw._id || raw.id || null;
    return {
      ...raw,
      _id: normalizedId,
      id: normalizedId
    };
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // CRITICAL: Only run auth initialization once
      if (authInitializedRef.current) {
        console.log('CustomerAuth: Already initialized, skipping');
        return;
      }
      authInitializedRef.current = true;

      console.log('CustomerAuth: Initializing authentication...');
      try {
        // Check if there's an existing session by calling the refresh endpoint
        // This will validate the HTTP-only cookie and return customer data if valid
        const data = await apiCall('/customer/auth/refresh', {
          method: 'POST'
        });

        if (data.customer) {
          console.log('CustomerAuth: Valid session found, restoring customer data');
          setCustomer(normalizeCustomer(data.customer));
          setIsAuthenticated(true);
        } else {
          // No session found, but this is expected for logged-out users
          console.log('CustomerAuth: No active session found');
          // Don't call setIsAuthenticated(false) here if we're already authenticated
          // This prevents clearing auth state that was just set by login
        }
      } catch (error) {
        console.error('CustomerAuth: Error initializing authentication:', error);
        console.log('CustomerAuth: Error status:', error.status);
        console.log('CustomerAuth: Error message:', error.message);

        // FIXED: Properly detect authentication errors vs network errors
        // Check for 401 status OR common "no session" error messages
        const isAuthError = error.status === 401 ||
                           error.message?.includes('No session found') ||
                           error.message?.includes('Not authenticated') ||
                           error.message?.includes('Invalid session');

        if (isAuthError) {
          console.log('CustomerAuth: No valid session (expected for logged-out users)');
          // This is normal for users who aren't logged in - don't clear anything
          // Just ensure isAuthenticated is false
          if (isAuthenticated) {
            console.log('CustomerAuth: Clearing stale auth state');
            clearAuth();
          }
        } else {
          // Real network error - preserve auth state
          console.log('CustomerAuth: Network error during init, preserving current auth state');
        }
      } finally {
        setLoading(false);
        console.log('CustomerAuth: Initialization complete');
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateSession = async (sessionId) => {
    try {
      const data = await apiCall(`/customer/auth/validate-session/${sessionId}`, {
        method: 'GET'
      });

      if (data.valid && data.customer) {
        setCustomer(normalizeCustomer(data.customer));
        setIsAuthenticated(true);
        console.log('CustomerAuth: Session validated successfully');
        return true;
      } else {
        console.log('CustomerAuth: Session invalid or no customer data');
        return false;
      }
    } catch (error) {
      console.error('CustomerAuth: Session validation error:', error);
      // Return false but don't clear auth on network errors
      return false;
    }
  };

  const fetchCustomerProfile = async () => {
    try {
      const data = await apiCall('/customer/auth/profile', {
        method: 'GET'
      });
      return normalizeCustomer(data.customer);
    } catch (error) {
      console.error('CustomerAuth: Error fetching customer profile:', error);
      return null;
    }
  };

  const validateToken = async (tokenToValidate) => {
    try {
      const data = await apiCall('/customer/auth/profile', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenToValidate}`,
          'Content-Type': 'application/json'
        }
      });

      if (data.customer) {
        setCustomer(normalizeCustomer(data.customer));
        return true;
      }
      return false;
    } catch (error) {
      console.error('CustomerAuth: Token validation error:', error);
      return false;
    }
  };

  const login = async (email, password, storeSlug) => {
    try {
      console.log('CustomerAuthContext: Starting login...', { email, storeSlug });
      const data = await apiCall('/customer/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, storeSlug })
      });

      console.log('CustomerAuthContext: Login API response:', {
        hasCustomer: !!data.customer,
        hasToken: !!data.token,
        multipleStores: !!data.multipleStores
      });

      // Handle multiple stores response
      if (data.multipleStores) {
        console.log('CustomerAuthContext: Multiple stores detected, returning for selection');
        return { success: false, multipleStores: true, stores: data.stores, data };
      }

      if (data.customer) {
        console.log('CustomerAuthContext: Setting customer and auth state');
        setCustomer(normalizeCustomer(data.customer));
        setIsAuthenticated(true);
        console.log('CustomerAuthContext: Auth state updated successfully');
        // Don't store session data in localStorage for security
        // Sessions should be handled by secure HTTP-only cookies
        return { success: true, data };
      } else {
        console.error('CustomerAuthContext: No customer data in response');
        return { success: false, error: 'No customer data returned' };
      }
    } catch (error) {
      console.error('CustomerAuth: Login error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  };

  const register = async (userData, storeSlug) => {
    try {
      const data = await apiCall('/customer/auth/register', {
        method: 'POST',
        body: JSON.stringify({ ...userData, storeSlug })
      });

      setCustomer(normalizeCustomer(data.customer));
      setIsAuthenticated(true);
      // Don't store session data in localStorage for security
      // Sessions should be handled by secure HTTP-only cookies
      return { success: true, data };
    } catch (error) {
      console.error('CustomerAuth: Registration error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  };

  const logout = async () => {
    try {
      // Call backend to destroy session cookie
      await apiCall('/customer/auth/logout', {
        method: 'POST'
      });
    } catch (error) {
      console.error('CustomerAuth: Logout API error:', error);
    } finally {
      // Clear frontend state regardless of API call result
      clearAuth();
      window.location.href = '/login'; // Fixed: use /login instead of /customer/login
    }
  };

  const clearAuth = () => {
    setToken(null);
    setCustomer(null);
    setIsAuthenticated(false);
    // Clear any sensitive data from localStorage
    localStorage.removeItem('customerSessionId');
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerData');
    localStorage.removeItem('checkout_redirect_cart');
  };

  const refreshCustomerData = async () => {
    try {
      const customerData = await fetchCustomerProfile();
      if (customerData) {
        setCustomer(normalizeCustomer(customerData));
        return true;
      }
      return false;
    } catch (error) {
      console.error('CustomerAuth: Error refreshing customer data:', error);
      return false;
    }
  };

  const value = {
    customer,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    clearAuth,
    refreshCustomerData,
    validateToken,
    validateSession  // Add validateSession to context value
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};


